import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { CarService } from 'src/app/services/car/car.service';
import { PartsService } from 'src/app/services/parts/parts.service';
import { UploadComponent } from '../../layout/upload/upload.component';
import { environment } from '../../../../environments/environment';
import { SpinnerComponent } from '../../layout/spinner/spinner.component';
import { EditorModule } from '@tinymce/tinymce-angular';
import 'tinymce/tinymce.min.js';
import 'tinymce/icons/default/icons.min.js';
import 'tinymce/themes/silver/theme.min.js';

// TinyMCE plugins
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';
import 'tinymce/plugins/fullscreen';

import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { ScrollingModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-parts',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    NgbPaginationModule,
    UploadComponent,
    DragDropModule,
    SpinnerComponent,
    EditorModule,
    FormsModule,
    ScrollingModule,
  ],
  templateUrl: './parts.component.html',
  styleUrl: './parts.component.css'
})
export class PartsComponent {
  data: any[] = [];
  selectedCategory: string = '';
  searchTerm: string = '';

  filteredData: any[] = [];
  load = false;      // loading parts list
  loadCats = false;  // loading categories
  partForm: FormGroup;
  page = 1;
  perPage = 20;
  lastPage: number = 1;

  categories: any[] = [];

  url = environment.apiDomain;

  loadUpdate = false;
  itemSize = 80;

  constructor(
    private PartsService: PartsService,
    private CarService: CarService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private ToastrService: ToastrService,
  ) {
    this.partForm = this.fb.group({
      id: [''],
      title_ka: [''],
      title_en: [''],
      title_ru: [''],
      description_ka: [''],
      description_en: [''],
      description_ru: [''],
      numbersy: [''],
      image: [''],
      folder: [''],
      price: [''],
      catid: [''],
      newprice: [''],
      stock: [''],
      oem: [''],
      code: [''],
      compatible_with: ['']
    });

    this.get();
    this.getCategories();
  }

  get() {
    this.load = true;

    this.PartsService.get(this.page, this.perPage, this.selectedCategory, this.searchTerm).subscribe(
      (res: any) => {
        if (this.page === 1) {
          this.filteredData = res.data;
        } else {
          this.filteredData = [...this.filteredData, ...res.data];
        }

        

        // Guard: if backend response doesn't include data array, don't break UI
        if (!Array.isArray(this.filteredData)) {
          this.filteredData = [];
        }
        console.log(this.filteredData)
        // NOTE: search is handled server-side via ?search=...
        // Client-side filtering here can incorrectly hide results.

        this.lastPage = Number(res?.last_page ?? 1);
        this.load = false;
      },
      () => {
        this.load = false;
      }
    );
  }

  getCategories() {
    this.loadCats = true;
    this.CarService.get().subscribe(
      (res: any) => {
        this.categories = res;
        this.loadCats = false;
      },
      (error: any) => {
        this.loadCats = false;
        console.log(error);
      }
    );
  }

  filterByCategory() {
    this.page = 1;
    this.filteredData = [];
    this.get();
  }

  onSearchChange() {
    this.page = 1;
    this.filteredData = [];
    this.get();
  }

  clearSearch() {
    this.searchTerm = '';
    this.onSearchChange();
  }

  openModal(content: any) {
    this.partForm.reset();
    this.modalService.open(content, { size: 'lg' });
  }

  onFileUploaded(fileName: string) {
    this.partForm.patchValue({ image: fileName });
  }

  savePart(modal: any) {
    const partId = this.partForm.get('id')?.value;
    if (partId) {
      this.PartsService.update(partId, this.partForm.value).subscribe(
        () => {
          modal.close();
          this.page = 1;
          this.filteredData = [];
          this.get();
        },
        () => {}
      );
    } else {
      this.PartsService.create(this.partForm.value).subscribe(
        () => {
          modal.close();
          this.partForm.reset();
          this.page = 1;
          this.filteredData = [];
          this.get();
        },
        () => {}
      );
    }
  }

  editPart(part: any, modal: any) {
    this.partForm.patchValue(part);
    this.modalService.open(modal, { size: 'lg' });
  }

  deletePart(id: any) {
    if (confirm('ნამდვილათ გსურთ წაშლა')) {
      this.PartsService.delete(id).subscribe(
        () => {
          this.page = 1;
          this.filteredData = [];
          this.get();
        },
        (error: any) => {
          console.log(error);
        }
      );
    }
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.filteredData, event.previousIndex, event.currentIndex);

    this.filteredData = this.filteredData.map((item, index) => ({
      ...item,
      position: index + 1
    }));

    this.loadUpdate = true;
    this.PartsService.updatePositions(this.filteredData).subscribe(
      () => {
        this.loadUpdate = false;
        this.ToastrService.success('პოზიცია წარმატებით განახლდა');
      },
      () => {
        this.loadUpdate = false;
        this.ToastrService.error('მოხდა შეცდომა პოზიციის განახლების დროს');
      }
    );
  }

  getUploadPatch(folder: string) {
    if (!folder) return 'parts';
    return 'parts' + '/' + folder;
  }

  loadMore() {
    if (this.page < this.lastPage && !this.load) {
      this.page++;
      this.get();
    }
  }

  getImages(folder: string, image: string) {
    if (!folder) return this.url + 'parts/' + image;
    return this.url + 'parts/' + folder + '/' + image;
  }
}
